/**
 * AdminProductsPending — /admin/products/pending
 *
 * Lists every fp_products_master row with status="pending_review",
 * lets an admin approve / reject / attach a photo (from Open Food Facts
 * suggestions or Google Images) in one click.
 *
 * Backend: /api/inventory/master/pending, /master/{id}/approve,
 * /master/{id}/reject, /master/{id}/attach-photo, /master/{id}/suggest-photos
 */
import { useCallback, useEffect, useState } from "react";
import { http } from "@/lib/api";
import {
  CheckCircle2, XCircle, ImagePlus, ExternalLink, Loader2, RefreshCw,
  AlertTriangle, Search, Camera,
} from "lucide-react";

export default function AdminProductsPending() {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]       = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const r = await http.get("/inventory/master/pending", { params: { limit: 100 } });
      setItems(r.data.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (p) => {
    if (!window.confirm(`Approve "${p.name}"?`)) return;
    try {
      await http.post(`/inventory/master/${p.id}/approve`, {});
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const reject = async (p) => {
    const reason = window.prompt(`Reject "${p.name}" — reason?`);
    if (reason === null) return;
    try {
      await http.post(`/inventory/master/${p.id}/reject`, { reason });
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8" data-testid="admin-products-pending">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">
            <Camera size={12} className="inline mr-1.5 -mt-0.5" />
            PRODUCT MODERATION QUEUE
          </div>
          <h1 className="font-display text-3xl md:text-4xl leading-tight mt-1">Pending products</h1>
          <p className="text-sm mt-1 max-w-[64ch]" style={{ color: "var(--wofy-text-soft)" }}>
            Every product auto-stubbed from an unknown scan or a supplier feed lands here for admin review.
            Approve, reject, or suggest photos in one click.
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="inline-flex items-center gap-1.5 text-sm border rounded-full px-3 py-1.5 disabled:opacity-50"
                style={{ borderColor: "var(--wofy-line)" }}
                data-testid="admin-pending-refresh">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Refresh
        </button>
      </div>

      {err && (
        <div className="mb-4 rounded-lg p-3 text-sm" style={{ background: "#FEE2E2", color: "#8A1F1F" }}>
          <AlertTriangle size={14} className="inline mr-1.5" /> {err}
        </div>
      )}

      {loading ? (
        <div className="opacity-70 text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border p-8 text-center opacity-70" style={{ borderColor: "var(--wofy-line)" }}
             data-testid="admin-pending-empty">
          <CheckCircle2 size={28} className="mx-auto mb-2 opacity-40" />
          Queue is clear — no products pending review.
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-pending-list">
          {items.map((p) => (
            <PendingRow key={p.id} product={p} onApprove={approve} onReject={reject} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}


function PendingRow({ product: p, onApprove, onReject, onChange }) {
  const [photoOpen, setPhotoOpen] = useState(false);
  const first = (p.photos || [])[0]?.url || p.image_url;

  return (
    <div className="rounded-xl border p-4 flex flex-wrap gap-4" style={{ borderColor: "var(--wofy-line)" }}
         data-testid={`pending-${p.id}`}>
      <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden flex items-center justify-center"
           style={{ background: "#F2ECDE" }}>
        {first ? (
          <img src={first} alt={p.name} className="w-full h-full object-cover"
               onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <Camera size={28} className="opacity-30" />
        )}
      </div>
      <div className="flex-1 min-w-[280px]">
        <div className="flex items-baseline gap-2 flex-wrap">
          <div className="font-display text-lg">{p.name || "(untitled)"}</div>
          <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">
            {p.gtin ? `GTIN ${p.gtin}` : "no GTIN"}
          </span>
        </div>
        <div className="text-xs opacity-70 mt-0.5 space-x-2">
          {p.brand    && <span>{p.brand}</span>}
          {p.category && <span>· {p.category}</span>}
          {p.perishability_class && <span>· {p.perishability_class}</span>}
        </div>
        <div className="text-[11px] opacity-50 mt-1">
          stubbed {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
        </div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button onClick={() => onApprove(p)}
                  className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 text-white"
                  style={{ background: "#3E5A2A" }}
                  data-testid={`pending-${p.id}-approve`}>
            <CheckCircle2 size={12} /> Approve
          </button>
          <button onClick={() => onReject(p)}
                  className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border"
                  style={{ borderColor: "#B04A3E", color: "#B04A3E" }}
                  data-testid={`pending-${p.id}-reject`}>
            <XCircle size={12} /> Reject
          </button>
          <button onClick={() => setPhotoOpen(!photoOpen)}
                  className="inline-flex items-center gap-1 text-xs rounded-full px-3 py-1.5 border"
                  style={{ borderColor: "var(--wofy-line)" }}
                  data-testid={`pending-${p.id}-photos`}>
            <ImagePlus size={12} /> {photoOpen ? "Close" : "Suggest photos"}
          </button>
        </div>
      </div>
      {photoOpen && <PhotoSuggestPanel product={p} onSaved={onChange} onClose={() => setPhotoOpen(false)} />}
    </div>
  );
}


function PhotoSuggestPanel({ product, onSaved, onClose }) {
  const [busy, setBusy] = useState(true);
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState("");
  useEffect(() => {
    (async () => {
      setBusy(true); setErr("");
      try {
        const r = await http.get(`/inventory/master/${product.id}/suggest-photos`);
        setData(r.data);
      } catch (e) { setErr(e?.response?.data?.detail || e.message); }
      finally { setBusy(false); }
    })();
  }, [product.id]);

  const attach = async (url, alt = "") => {
    try {
      await http.post(`/inventory/master/${product.id}/attach-photo`, null,
                       { params: { photo_url: url, alt } });
      onSaved && onSaved();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="basis-full rounded-lg border p-3 mt-2" style={{ borderColor: "var(--wofy-line)", background: "#FBFBF6" }}
         data-testid={`pending-${product.id}-photo-panel`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Photo candidates</div>
        <a href={data?.google_images} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1 text-[11px] underline"
           data-testid={`pending-${product.id}-google-images`}>
          <Search size={11} /> Open Google Images
        </a>
      </div>
      {busy && <Loader2 size={14} className="animate-spin opacity-60" />}
      {err && <div className="text-xs" style={{ color: "#B04A3E" }}>{err}</div>}
      {!busy && data && (
        <>
          {data.candidates.length === 0 ? (
            <div className="text-xs opacity-70">
              No auto-suggested photos (Open Food Facts had no match). Click "Open Google Images" above,
              copy an image URL, and paste it below.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {data.candidates.map((c, i) => (
                <button key={i} onClick={() => attach(c.url, c.label)}
                        className="group rounded overflow-hidden border hover:shadow-md"
                        style={{ borderColor: "var(--wofy-line)" }}
                        data-testid={`pending-${product.id}-candidate-${i}`}>
                  <img src={c.url} alt={c.label}
                       className="w-full h-24 object-cover"
                       onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  <div className="px-2 py-1 text-[10px] opacity-70 flex items-center justify-between">
                    <span className="truncate">{c.source}</span>
                    <span className="opacity-0 group-hover:opacity-100 font-semibold">Attach →</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          <ManualPasteRow onAttach={attach} testid={`pending-${product.id}-manual-paste`} />
          <button onClick={onClose}
                  className="mt-2 text-[11px] opacity-60 hover:opacity-100">Close panel</button>
        </>
      )}
    </div>
  );
}


function ManualPasteRow({ onAttach, testid }) {
  const [url, setUrl] = useState("");
  return (
    <div className="mt-3 flex items-center gap-2">
      <ExternalLink size={12} className="opacity-50" />
      <input value={url} onChange={(e) => setUrl(e.target.value)}
             placeholder="Paste an image URL from Google Images…"
             className="flex-1 border rounded px-2 py-1 text-xs"
             style={{ borderColor: "var(--wofy-line)" }}
             data-testid={`${testid}-input`} />
      <button disabled={!url.trim()}
              onClick={() => { onAttach(url.trim()); setUrl(""); }}
              className="text-xs font-semibold rounded-full px-3 py-1 text-white disabled:opacity-50"
              style={{ background: "var(--wofy-primary)" }}
              data-testid={`${testid}-btn`}>
        Attach
      </button>
    </div>
  );
}
